/**
 * Recovery from stale lazy chunks after a redeploy.
 *
 * An open tab still references the previous build's hashed chunks (`/assets/VotersTab-<old>.js`), which no longer
 * exist on the server, so the next lazy route import fails. A full page reload fetches the new index.html and
 * chunks. The reload is guarded with a sessionStorage timestamp so a genuinely broken deploy cannot loop: at most one
 * automatic reload per {@link STALE_CHUNK_RELOAD_WINDOW_MS}; after that the route error boundary offers a manual
 * "Reload" button instead.
 */

export const STALE_CHUNK_RELOAD_KEY = 'de:stale-chunk-reload-at'
export const STALE_CHUNK_RELOAD_WINDOW_MS = 30_000

const CHUNK_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i, // Chromium
  /error loading dynamically imported module/i, // Firefox
  /importing a module script failed/i, // Safari
  /unable to preload css/i, // Vite's CSS preload
  /'text\/html' is not a valid javascript mime type/i,
]

/** True for the errors browsers / Vite raise when a lazily imported chunk cannot be loaded. */
export function isChunkLoadError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : typeof err === 'string' ? err : ''
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(message))
}

export interface StaleChunkReloaderDeps {
  storage: Pick<Storage, 'getItem' | 'setItem'> | undefined
  now: () => number
  reload: () => void
  windowMs?: number
}

/**
 * Returns a function that reloads the page once and reports whether a reload is under way (`true`: the caller should
 * wait for the navigation; `false`: give up and surface the error). Repeated calls while a reload is pending are
 * deduplicated. Without usable storage there is no loop guard, so it never reloads automatically.
 */
export function createStaleChunkReloader(deps: StaleChunkReloaderDeps): () => boolean {
  const windowMs = deps.windowMs ?? STALE_CHUNK_RELOAD_WINDOW_MS
  let reloading = false
  return () => {
    if (reloading) return true
    try {
      if (!deps.storage) return false
      const now = deps.now()
      const last = Number(deps.storage.getItem(STALE_CHUNK_RELOAD_KEY))
      if (Number.isFinite(last) && last > 0 && now - last < windowMs) return false
      deps.storage.setItem(STALE_CHUNK_RELOAD_KEY, String(now))
    } catch {
      return false // storage disabled or full: no guard, no automatic reload
    }
    reloading = true
    deps.reload()
    return true
  }
}

function browserStorage(): Storage | undefined {
  try {
    return window.sessionStorage
  } catch {
    return undefined
  }
}

let browserReloader: (() => boolean) | undefined

/** Reloads the page at most once per window (see {@link createStaleChunkReloader}). */
export function reloadForStaleChunk(): boolean {
  browserReloader ??= createStaleChunkReloader({
    storage: browserStorage(),
    now: Date.now,
    reload: () => window.location.reload(),
  })
  return browserReloader()
}

/** Vite dispatches `vite:preloadError` when a dynamic import or its preloaded CSS fails. */
export function installStaleChunkRecovery() {
  window.addEventListener('vite:preloadError', (event) => {
    // Not `preventDefault()`: the import must still reject so the caller does not run with an undefined module.
    if (reloadForStaleChunk()) console.warn('Stale chunk after a redeploy, reloading the page', event.payload)
  })
}

/** Never settles: used while the page is reloading so the router keeps the current UI instead of an error. */
export const pendingForever = <T>() => new Promise<T>(() => {})
